import { useMutation, useQuery } from '@tanstack/react-query';
import { QK } from '@api/queryKeys';

import { bexioApi } from './bexio';
import type {
  BexioDisconnectBody,
  BexioExchangeTokenBody,
  BexioExchangeTokenResponse,
  BexioExportResponse,
  BexioRefetchResponse,
} from './types';

export const useBexioLogin = () =>
  useMutation<string, Error, void>({
    mutationFn: () => bexioApi.login(),
  });

export const useBexioExchangeToken = () =>
  useMutation<BexioExchangeTokenResponse, Error, { code: string; body: BexioExchangeTokenBody }>({
    mutationFn: ({ code, body }) => bexioApi.exchangeToken(code, body),
  });

export const useBexioRefetch = () =>
  useMutation<BexioRefetchResponse, Error, { auditId: string; targetId: string | number }>({
    mutationFn: ({ auditId, targetId }) => bexioApi.refetch(auditId, targetId),
  });

export const useBexioRefetchStatus = (auditId: string, targetId: string | number, enabled = true) =>
  useQuery<BexioRefetchResponse | null, Error>({
    queryKey: QK.bexioRefetchStatus(auditId, targetId),
    queryFn: () => bexioApi.refetchStatus(auditId, targetId),
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 2000 : false;
    },
  });

export const useBexioExport = () =>
  useMutation<BexioExportResponse, Error, { auditId: string; targetId: string | number }>({
    mutationFn: ({ auditId, targetId }) => bexioApi.exportData(auditId, targetId),
  });

export const useBexioExportStatus = (
  auditId: string,
  targetId: string | number,
  enabled = true,
  jobId?: number | null,
) =>
  useQuery<BexioExportResponse | null, Error>({
    queryKey: QK.bexioExportStatus(auditId, targetId, jobId),
    queryFn: () => bexioApi.exportStatus(auditId, targetId, jobId),
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 2000 : false;
    },
  });

export const useBexioDisconnect = () =>
  useMutation<void, Error, BexioDisconnectBody>({
    mutationFn: (body) => bexioApi.disconnect(body),
  });
